import { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/stores/store';
import {
  setThreads,
  addThread,
  setCurrentThread,
  clearMessages,
  loadMessages,
  setThreadLoading,
  clearCustomEventValues,
  setCustomEventValues,
  setCurrentTeam,
  setTeamLeader,
  updateThreadTitleInStore,
  addIntermediateStep,
  resetChatState,
  resetFullChatState,
  removeThread,
} from '@/stores/slices/chat-slice';
import {
  selectThreads,
  selectCurrentThreadId,
  selectThreadLoading,
} from '@/stores/selectors/chat-selectors';
import { selectSelectedAgent } from '@/stores/selectors/agent-selectors';
import { chatAssistantService } from '@/services/chat-assistant-service';
import { useOptionalActions } from '@/providers/actions-provider';
import { getLangGraphClient } from '@/lib/langgraph-client-v2';
import {
  convertLangGraphMessagesToRedux,
  convertApiEventsToRedux,
  extractIntermediateStepsFromMessages,
} from '@/utils/thread-converters';
import type { ThreadMetadata } from '@/types/langgraph';

/**
 * Custom hook for thread management
 * Provides methods to create, switch, and manage conversation threads
 */
export function useThreads() {
  const dispatch = useAppDispatch();
  const threads = useAppSelector(selectThreads);
  const currentThreadId = useAppSelector(selectCurrentThreadId);
  const threadsLoading = useAppSelector(selectThreadLoading);
  const selectedAgent = useAppSelector(selectSelectedAgent);
  const actionsCtx = useOptionalActions();

  /**
   * Create a new thread
   */
  const createThread = useCallback(
    async (
      userId: string,
      metadata?: Record<string, any>
    ): Promise<[Error | null, ThreadMetadata | null]> => {
      dispatch(setThreadLoading(true));

      const [error, thread] = await chatAssistantService.createThread(userId, metadata);

      if (!error && thread) {
        dispatch(addThread(thread));
        dispatch(setCurrentThread(thread.langGraphThreadId));
      }

      dispatch(setThreadLoading(false));
      return [error, thread];
    },
    [dispatch]
  );

  /**
   * Switch to a different thread
   */
  const switchThread = useCallback(
    (threadId: string) => {
      dispatch(setCurrentThread(threadId));
      dispatch(clearMessages());
      dispatch(clearCustomEventValues());
      dispatch(setCurrentTeam(null));
      dispatch(setTeamLeader(null));

      const client = selectedAgent ? getLangGraphClient(selectedAgent.id) : undefined;

      // Load thread history (NOW INCLUDES UI EVENTS AND METADATA)
      chatAssistantService.getThreadHistory(threadId, client).then(([error, data]) => {
        if (!error && data) {
          // Convert and load messages
          const reduxMessages = convertLangGraphMessagesToRedux(data.messages);
          dispatch(loadMessages(reduxMessages));

          // Rebuild intermediate tool_call steps from history so that tool-
          // rendering components (e.g. api_key_submit_ui form) can still
          // resolve their originating tool call via getLastToolCall() after
          // a thread switch. Without this they would see no tool_call and
          // bail with "No tool call found".
          const replayedSteps = extractIntermediateStepsFromMessages(
            data.messages,
          );
          for (const step of replayedSteps) {
            dispatch(addIntermediateStep(step));
          }

          // Convert and load custom events
          const reduxEvents = convertApiEventsToRedux(data.uiEvents);
          if (reduxEvents.length > 0) {
            dispatch(setCustomEventValues(reduxEvents));
            console.log(`💾 [SWITCH-THREAD] Loaded ${reduxEvents.length} custom events`);
          }

          // Load current team and team leader from metadata
          dispatch(setCurrentTeam(data.currentTeam));
          dispatch(setTeamLeader(data.teamLeader));
        }
      });
    },
    [dispatch, selectedAgent]
  );

  /**
   * Load threads list (you may want to implement threads.search in the client)
   */
  const loadThreads = useCallback(
    (threadList: ThreadMetadata[]) => {
      dispatch(setThreads(threadList));
    },
    [dispatch]
  );

  // (``selectedAgent`` is already declared at the top of this hook
  // — line 42 — feature-develop redeclared it here in its merge but
  // we drop the duplicate.)

  /**
   * Fetch the last N threads from the backend and populate the store.
   * Pinned to the currently selected agent so the migration sidebar
   * never shows cleanup threads (or vice versa). Falls back to the
   * default client when no agent is selected yet (first paint).
   */
  const fetchRecentThreads = useCallback(async () => {
    const client = selectedAgent?.id
      ? getLangGraphClient(selectedAgent.id)
      : undefined;
    const [error, fetched] = await chatAssistantService.fetchRecentThreads(
      5,
      client,
    );
    if (!error && fetched) dispatch(setThreads(fetched));
  }, [dispatch, selectedAgent?.id]);

  // Auto-fetch whenever the selected agent resolves or changes. Two
  // bugs this fixes vs. the previous "sidebar calls once on mount":
  //   1. First paint races setSelectedAgent — the manual call fired
  //      before Redux had the resolved agent and ended up using the
  //      generic LangGraph 'agent' assistantId.
  //   2. Switching agents kept the previous agent's recent-threads
  //      list (the sidebar's [] deps effect never re-ran).
  // We also clear the threads slice so we don't flash the old
  // agent's list while the new fetch is in flight.
  useEffect(() => {
    if (!selectedAgent?.id) return;
    dispatch(setThreads([]));
    fetchRecentThreads();
  }, [selectedAgent?.id, fetchRecentThreads, dispatch]);

  /**
   * Clear state to start a new chat without an active thread.
   *
   * Also marks the URL with ``?new=1`` so the AssistantView mount-effect
   * (which on bare URLs would call ``loadLastAgentThread`` and resurrect
   * the previous conversation) goes down the "reset" branch instead.
   * Without this flag, clicking "New chat" felt like a no-op: the
   * sidebar's last-thread polling would race the clear and snap the
   * UI back to whatever thread Redux had a moment ago.
   */
  const newChat = useCallback(() => {
    dispatch(setCurrentThread(null));
    dispatch(clearMessages());
    dispatch(clearCustomEventValues());
    dispatch(setCurrentTeam(null));
    dispatch(setTeamLeader(null));
    actionsCtx?.clearActions();
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      params.delete('threadId');
      params.set('new', '1');
      const next = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({}, '', next);
    }
  }, [dispatch, actionsCtx]);

  /**
   * Soft-delete a thread from the backend and remove it from the Redux store.
   * If the deleted thread is the active one, resets chat state.
   */
  const deleteThread = useCallback(
    async (threadId: string) => {
      const client = selectedAgent ? getLangGraphClient(selectedAgent.id) : undefined;
      const [err] = await chatAssistantService.deleteThread(threadId, client);
      if (!err) {
        dispatch(removeThread(threadId));
        if (threadId === currentThreadId) {
          const remaining = threads.filter((t) => t.langGraphThreadId !== threadId);
          if (remaining.length > 0) {
            switchThread(remaining[0].langGraphThreadId);
          } else {
            newChat();
          }
        }
      }
    },
    [dispatch, selectedAgent, currentThreadId, threads, switchThread, newChat]
  );

  /**
   * Update a thread's title in the backend and sync the Redux store
   */
  const updateTitle = useCallback(
    async (threadId: string, title: string) => {
      if (!selectedAgent?.id) return;
      const [err] = await chatAssistantService.updateThreadTitle(threadId, title, selectedAgent.id);
      if (!err) {
        dispatch(updateThreadTitleInStore({ threadId, title }));
      }
    },
    [dispatch, selectedAgent?.id]
  );

  /**
   * Fetch threads for the given agent and load the most recent one.
   * Resets all chat state first so stale data from a previous agent is gone.
   * If the agent has no threads, leaves the chat blank (thread is created on
   * first message via the normal lazy-creation path).
   */
  const loadLastAgentThread = useCallback(
    async (agentId: string) => {
      dispatch(resetFullChatState());
      dispatch(setCurrentTeam(null));
      dispatch(setTeamLeader(null));

      const client = getLangGraphClient(agentId);
      const [error, fetched] = await chatAssistantService.fetchRecentThreads(5, client);

      if (!error && fetched && fetched.length > 0) {
        dispatch(setThreads(fetched));

        const lastThread = fetched[0];
        dispatch(setCurrentThread(lastThread.langGraphThreadId));
        dispatch(clearMessages());

        chatAssistantService.getThreadHistory(lastThread.langGraphThreadId, client).then(
          ([err, data]) => {
            if (!err && data) {
              dispatch(loadMessages(convertLangGraphMessagesToRedux(data.messages)));
              const replayedSteps = extractIntermediateStepsFromMessages(data.messages);
              for (const step of replayedSteps) dispatch(addIntermediateStep(step));
              const reduxEvents = convertApiEventsToRedux(data.uiEvents);
              if (reduxEvents.length > 0) dispatch(setCustomEventValues(reduxEvents));
              dispatch(setCurrentTeam(data.currentTeam));
              dispatch(setTeamLeader(data.teamLeader));
            }
          }
        );
      }
    },
    [dispatch]
  );

  return {
    threads,
    currentThreadId,
    threadsLoading,
    createThread,
    switchThread,
    loadThreads,
    fetchRecentThreads,
    newChat,
    updateTitle,
    deleteThread,
    loadLastAgentThread,
  };
}
